import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import './TimeSelector.css';

const ITEM_HEIGHT = 40;

const TimeSelector = ({ value, onChange, step = 15, minTime, maxTime }) => {
    const hourRef = useRef(null);
    const minuteRef = useRef(null);

    const currentHour = value ? parseInt(value.split(':')[0], 10) : null;
    const currentMinute = value ? parseInt(value.split(':')[1], 10) : null;

    const hours = useMemo(() => {
        const arr = [];
        for (let h = 0; h < 24; h++) {
            arr.push(h);
        }
        return arr;
    }, []);

    const minutes = useMemo(() => {
        const arr = [];
        const s = Math.max(1, step);
        for (let m = 0; m < 60; m += s) {
            arr.push(m);
        }
        return arr;
    }, [step]);

    const isTimeDisabled = useCallback((hour, minute) => {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        if (minTime && timeStr < minTime) return true;
        if (maxTime && timeStr > maxTime) return true;
        return false;
    }, [minTime, maxTime]);

    const scrollToSelected = useCallback((ref, index) => {
        if (ref.current && index >= 0) {
            const scrollTop = index * ITEM_HEIGHT - (ref.current.clientHeight / 2 - ITEM_HEIGHT / 2);
            ref.current.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        if (currentHour !== null) {
            const idx = hours.indexOf(currentHour);
            scrollToSelected(hourRef, idx);
        }
    }, [currentHour, hours, scrollToSelected]);

    useEffect(() => {
        if (currentMinute !== null) {
            const idx = minutes.indexOf(currentMinute);
            scrollToSelected(minuteRef, idx);
        }
    }, [currentMinute, minutes, scrollToSelected]);

    const handleHourClick = useCallback((hour) => {
        const min = currentMinute !== null ? currentMinute : minutes[0];
        if (isTimeDisabled(hour, min)) return;
        const newTime = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
        onChange(newTime);
    }, [currentMinute, minutes, onChange, isTimeDisabled]);

    const handleMinuteClick = useCallback((minute) => {
        const hr = currentHour !== null ? currentHour : 0;
        if (isTimeDisabled(hr, minute)) return;
        const newTime = `${String(hr).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        onChange(newTime);
    }, [currentHour, onChange, isTimeDisabled]);

    return (
        <div className="tsel-container">
            <div className="tsel-labels">
                <span className="tsel-label">Godz.</span>
                <span className="tsel-label">Min.</span>
            </div>
            <div className="tsel-columns">
                <div className="tsel-column" ref={hourRef}>
                    {hours.map(h => {
                        const disabled = isTimeDisabled(h, currentMinute !== null ? currentMinute : 0);
                        const classes = ['tsel-item'];
                        if (h === currentHour) classes.push('tsel-item--selected');
                        if (disabled) classes.push('tsel-item--disabled');

                        return (
                            <button
                                key={h}
                                type="button"
                                className={classes.join(' ')}
                                onClick={() => handleHourClick(h)}
                                disabled={disabled}
                            >
                                {String(h).padStart(2, '0')}
                            </button>
                        );
                    })}
                </div>
                <div className="tsel-divider" />
                <div className="tsel-column" ref={minuteRef}>
                    {minutes.map(m => {
                        const disabled = isTimeDisabled(currentHour !== null ? currentHour : 0, m);
                        const classes = ['tsel-item'];
                        if (m === currentMinute) classes.push('tsel-item--selected');
                        if (disabled) classes.push('tsel-item--disabled');

                        return (
                            <button
                                key={m}
                                type="button"
                                className={classes.join(' ')}
                                onClick={() => handleMinuteClick(m)}
                                disabled={disabled}
                            >
                                {String(m).padStart(2, '0')}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default TimeSelector;
